import { Checkbox } from 'primereact/checkbox'
import React, { useEffect, useState, useRef } from 'react'
import { BankAccount, AccountingAccount, Accounting, AccountTypeDTO, BankAccountDTO } from '../../interface/interface';
import { useIntl } from 'react-intl';
import { Combobox, ComboboxRef, CustomTable, InputTextCustom, Modal, ResponseDTO, TextArea, stringFilterTemplate, useFormValidation } from 'medere-ui-framework';
import { getAllAccountingAccountsByLevel, getAllAccountings } from "../../servicesHooks/Accounting/useAccountingService"
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { CustomTableRef } from 'medere-ui-framework';
import { validateCBU } from '../../servicesHooks/CBUValidator/useCBUValidatorService';
import { ComboDataDTO, defaultComboDataDTO } from '../../dto/ComboDataDTO';
import { Toast, ToastMessage } from 'primereact/toast'
import { Divider } from 'primereact/divider';
import { useBankService } from '../../servicesHooks/Bank/useBankService';
import { useSupplierService } from '../../servicesHooks/Supplier/useSupplierService';


export enum EAccountTypeCategory {
  BANCO = 1,
  TARJETA = 2,
  FONDO_PROPIO = 3
}

const showMessage = (ref: React.RefObject<Toast>, msg: any, severity: ToastMessage['severity']) => {
  ref.current?.show({ severity: severity, summary: msg, life: 3000 });
};

const initialStateBankAccount: BankAccount = {
  active: true,
  bankaccount: 0,
  bank: defaultComboDataDTO,
  accounttype: null,
  currency: defaultComboDataDTO,
  cbu: "",
  alias: "",
  notes: "",
  medereentity: 0,
  accountingaccount: null,
  paymentmethodbanks: []
}

export default function BankAccountsView() {
  const {
    deleteBankAccount,
    getAllAccountTypes,
    getAllBankAccountsByMedereEntity,
    getAllBanksForCombo,
    getAllCurrencies,
    getPaymentMethodBanks,
    saveBankAccount,
    updateBankAccount
  } = useBankService();
  const { getPaymentMethods } = useSupplierService();

  const [bankAccount, setBankAccount] = useState<BankAccount>({ ...initialStateBankAccount })
  const [accounting, setAccounting] = useState<Accounting>()

  const { handleValidation, errors } = useFormValidation(bankAccount)

  const intl = useIntl()
  const tableRef = useRef<CustomTableRef>(null)
  const toast = useRef<any>(null)
  const paymentMethodRef = useRef<any>(null)
  const [confirmDialog, setConfirmDialog] = useState(false)
  const [aliasError, setAliasError] = useState(false)
  const [cbuError, setCBUError] = useState(false)

  useEffect(() => {
    setBankAccount((prev) => ({
      ...prev,
      accountingaccount: null,
      medereentity: accounting?.company ?? 0,
    }))
  }, [accounting])

  useEffect(() => {
    if (bankAccount.accounttype?.accounttypecategory != EAccountTypeCategory.BANCO) {
      setBankAccount((prev) => ({ ...prev, cbu: "", alias: "" }))
    }
  }, [bankAccount.accounttype])

  function handleValidateCbu(): boolean {
    const validationResponse = validateCBU(bankAccount.cbu);
    if (!validationResponse.success) {
      showMessage(toast, 'El CBU no es válido', 'error')
      setCBUError(true)
    } else {
      showMessage(toast, 'El CBU es válido', 'success')
      setCBUError(false)
    }
    return validationResponse.success
  }

  function hasErrors() {
    return handleValidation(() => { }, errors, ['paymentmethodbanks', 'notes', 'active', 'cbu', 'bankaccount', 'alias']).invalidFields.length > 0
  }

  function convertBankAccountToDTO(bankAccount: BankAccount): BankAccountDTO {
    return {
      bankaccount: bankAccount.bankaccount,
      bank: bankAccount.bank?.id ?? 0,
      bankdesc: bankAccount.bank?.name ?? '',
      accounttype: bankAccount.accounttype?.id ?? 0,
      accounttypedesc: bankAccount.accounttype?.description ?? '',
      accounttypecategory: bankAccount.accounttype?.accounttypecategory ?? 0,
      currency: bankAccount.currency?.id ?? 0,
      currencydesc: bankAccount.currency?.name ?? '',
      cbu: bankAccount.cbu,
      alias: bankAccount.alias,
      notes: bankAccount.notes,
      medereentity: bankAccount.medereentity,
      accountingaccount: bankAccount?.accountingaccount?.accountingaccount ?? 0,
      accountingaccountdesc: bankAccount?.accountingaccount?.fulldescription ?? '',
      paymentmethodbanks: bankAccount.paymentmethodbanks,
      active: bankAccount.active
    }
  }

  async function handleAddBankAccount() {
    const validCbu = bankAccount.accounttype?.accounttypecategory == EAccountTypeCategory.BANCO ? await handleValidateCbu() : true; //el handleValidateCbu dispara la notification
    if (bankAccount.accounttype?.accounttypecategory != EAccountTypeCategory.BANCO) {
      setBankAccount({ ...bankAccount, cbu: "", alias: "" })
    }

    const hasNotErrors = !hasErrors()

    if (hasNotErrors && validCbu)
      ((bankAccount?.bankaccount ?? 0) > 0 ? updateBankAccount : saveBankAccount)({ ...convertBankAccountToDTO(bankAccount), paymentmethodbanks: bankAccount.paymentmethodbanks.map((i) => i.paymentMethod) }).then((res: ResponseDTO) => {
        if (res.success) {
          setBankAccount((prev) => ({ ...prev, ...initialStateBankAccount }))
          setAliasError(false)
          setCBUError(false)
          tableRef.current?.refreshTable?.()
        }
        showMessage(toast, res.success ? "Se ha guardado correctamente" : "Ha ocurrido un error al guardar", res.success ? 'success' : 'error')
      })
    else {
      showMessage(toast, "Complete los campos solicitados", 'error')
    }
  }

  function handleEditBankAccount(bankAccount: BankAccountDTO) {
    const accountType: AccountTypeDTO = {
      id: bankAccount.accounttype,
      description: bankAccount.accounttypedesc,
      accounttypecategory: bankAccount.accounttypecategory
    }
    const bank: ComboDataDTO = {
      id: bankAccount.bank,
      name: bankAccount.bankdesc
    }
    const currency: ComboDataDTO = {
      id: bankAccount.currency,
      name: bankAccount.currencydesc
    }
    const accountingAccount: AccountingAccount = {
      accountingaccount: bankAccount.accountingaccount,
      accounting: accounting?.accounting ?? 0,
      description: bankAccount.accountingaccountdesc,
      alias: '',
      allowsmovements: true,
      identifier: '',
      level: '',
      parentaccountingaccountpk: 0,
      fulldescription: bankAccount.accountingaccountdesc,
    }
    getPaymentMethodBanks(bankAccount?.bankaccount ?? -1).then((res) => {
      setBankAccount((prev) => (
        {
          ...prev,
          active: bankAccount.active ?? false,
          alias: bankAccount.alias ?? '',
          cbu: bankAccount.cbu ?? '',
          bankaccount: bankAccount.bankaccount ?? 0,
          medereentity: bankAccount.medereentity ?? 0,
          accountingaccount: accountingAccount,
          notes: bankAccount.notes ?? '',
          paymentmethodbanks: res.map((i:any) => ({ paymentMethod: i.paymentmethod, methodName: i.paymentmethoddesc })),
          accounttype: accountType,
          bank,
          currency
        }

      ))
    })
  }

  function handleDeleteBankAccount(bankAccountId: number) {
    deleteBankAccount(bankAccountId).then(() => {
      if (tableRef.current?.refreshTable) {
        tableRef.current?.refreshTable
      }
      setBankAccount({ ...initialStateBankAccount })
    })

  }

  return <div className='flex flex-column h-full p-4 viewContainer'>
    <h2 style={{ color: '#B67345', fontWeight: 600 }} className="text-2xl mb-3">Cuentas y Fondos</h2>
    <Combobox
      getItems={getAllAccountings}
      label="Plan de cuentas"
      optionLabel="description"
      floatLabel
      value={accounting}
      setValue={(e) => {
        setAccounting(e)
        setBankAccount((prev) => ({ ...prev, medereentity: e?.company }))
      }}
    />
    <Divider className='emr-divider-soft-2' />
    <Modal
      visible={confirmDialog}
      setVisible={setConfirmDialog}
      header={"¿Confirma eliminar la cuenta bancaria?"}
      footerButtonRightText={intl.formatMessage({ id: "Yes" })}
      footerButtonLeftText={intl.formatMessage({ id: "No" })}
      onClickRightBtn={() => {
        setConfirmDialog(false);
        if (bankAccount.medereentity != null && bankAccount.medereentity > -1) {
          handleDeleteBankAccount(bankAccount.bankaccount ?? -1);
        }
      }}
      onClickLeftBtn={() => { setConfirmDialog(false); }} />
    <div className='p-2 flex flex-row align-items-center -mb-2'>
      <Checkbox className='flex mr-2' checked={bankAccount.active} onChange={() => {
        setBankAccount({ ...bankAccount, active: !bankAccount.active })
      }} />
      <p className='flex font-medium text-sm line-height-auto text-600'>{intl.formatMessage({ id: "active" })}</p>
    </div>
    <div className='flex flex-column md:flex-row align-items-baseline -mb-2'>
      <Combobox
        label={"Banco"}
        floatLabel
        error={errors.bank}
        className="md:w-3 mr-0 md:mr-2 mb-4 md:mb-0"
        getItems={getAllBanksForCombo}
        optionLabel='name' setValue={(e: ComboDataDTO) => {
          setBankAccount((prev) => ({ ...prev, bank: e ? e : defaultComboDataDTO }))
        }}
        value={bankAccount.bank} />
      <Combobox
        label={"Tipo de cuenta"}
        floatLabel
        error={errors.accounttype}
        className="md:w-3 mr-0 md:mr-2 mb-4 md:mb-0"
        getItems={getAllAccountTypes}
        optionLabel='description'
        setValue={(e: AccountTypeDTO) => {
          setBankAccount((prev) => ({ ...prev, accounttype: e }))
        }}
        value={bankAccount.accounttype} />
      <Combobox
        label="Moneda"
        floatLabel
        error={errors.currency}
        className="md:w-2 mr-0 md:mr-2 mb-4 md:mb-0"
        getItems={getAllCurrencies}
        optionLabel='name' setValue={(e: ComboDataDTO | null) => {
          setBankAccount((prev) => ({ ...prev, currency: e }))
        }}
        value={bankAccount.currency}
      />
      {bankAccount.accounttype && bankAccount.accounttype.accounttypecategory == EAccountTypeCategory.BANCO && <InputTextCustom value={bankAccount.alias}
        onChange={(e) => {
          setBankAccount({ ...bankAccount, alias: e.target.value })
          setAliasError(false)
        }}
        label={"Alias"}
        error={aliasError}
        floatLabel
        className='md:w-3'
      />}
    </div>
    <div className='flex flex-column md:flex-row align-items-baseline '>
      <Combobox
        label="Cuenta contable"
        floatLabel
        error={errors.accountingaccount}
        getItems={getAllAccountingAccountsByLevel}
        params={{
          accounting: accounting?.accounting,
          level: accounting?.treesize,
        }}
        placeholder="Ejemplo: xxxxx (X.X.X)"
        optionLabel="fulldescription"
        setValue={(e) => {
          setBankAccount((prev) => ({ ...prev, accountingaccount: e }))
        }}
        value={bankAccount.accountingaccount}
        disable={!accounting} />

      {bankAccount.accounttype && bankAccount.accounttype.accounttypecategory == EAccountTypeCategory.BANCO && <div className='flex flex-row align-items-baseline w-full'>
        <InputTextCustom
          value={bankAccount.cbu}
          onChange={(e) => {
            setBankAccount({ ...bankAccount, cbu: e.target.value })
            setCBUError(false)
          }}
          label={"CBU"}
          error={cbuError}
          floatLabel
          className='md:ml-2'
        />
        <Button
          label='Validar'
          style={{ width: '175px' }}
          className='buttonMain2 p-button-rounded ml-2'
          onClick={handleValidateCbu} />
      </div>}
    </div>
    <Combobox
      getItems={getPaymentMethods}
      multiple
      floatLabel
      label="Medios de pago"
      optionLabel='methodName'
      value={bankAccount.paymentmethodbanks}
      ref={paymentMethodRef}
      setValue={(e) => {
        setBankAccount((prev) => {
          // remove duplicates
          e = e.filter((item:any, index:number) => e.findIndex((i:any) => i.paymentMethod === item.paymentMethod) === index)
          return ({ ...prev, paymentmethodbanks: e })
        })
      }}
    />
    <div className='flex flex-row align-items-center'>
      <TextArea label={"Notas"} className="" autoResize value={bankAccount.notes} onChange={(e: any) => {
        setBankAccount({ ...bankAccount, notes: e.target.value })
      }}
      />
      <Button
        icon={bankAccount.bankaccount ?? -1 > -1 ? "pi pi-check" : "pi pi-plus"}
        rounded
        className='buttonMain p-button-rounded ml-2'
        onClick={handleAddBankAccount} />
    </div>
    <CustomTable
      getItems={getAllBankAccountsByMedereEntity}
      params={{ medereentity: accounting?.company, }}
      idTable='bankaccounts-table'
      ref={tableRef}
      notFlex
      maxHeight={400}
      rowButtonClassName="buttonSecondary2TableButtons"
      buttonController
      dropdownButton={{
        edit: handleEditBankAccount, deleted: (ba: BankAccount) => {
          setConfirmDialog(true);
          setBankAccount({ ...bankAccount, bankaccount: ba.bankaccount })
        }
      }}
    >
      <Column field="bankdesc" header="Banco" filter filterElement={stringFilterTemplate} />
      <Column field="cbu" header="CBU" filter filterElement={stringFilterTemplate} />
      <Column field="alias" header="Alias" filter filterElement={stringFilterTemplate} />
      <Column field="accounttypedesc" header="Tipo de cuenta" filter filterElement={stringFilterTemplate} />
      <Column field="accountingaccountdesc" header="Cuenta contable" filter filterElement={stringFilterTemplate} />
      <Column field="currencydesc" header="Moneda" filter filterElement={stringFilterTemplate} />
      <Column field="notes" header="Notas" />
      <Column field="active" header="Activa" body={(e: BankAccount) => <i className={e.active ? "pi pi-check" : "pi pi-times"} />} />
    </CustomTable>
    <Toast ref={toast} position='bottom-right' />
  </div>
}
